import { useQuery } from "@tanstack/react-query";

import { axiosInstance, TRADEA } from "api";
import { getSelectedPowerPlant, useAppSelector } from "store";

import { TradeaIndex } from "./earnings";
import { usePricesDateStr } from "../prices/prices";

export interface CurtailmentQuarter {
  time: string;
  curtail: boolean;
  p50: number | null;
  probNegative: number | null;
  producedKwh: number | null;
  expectedKwh: number | null; // production the quarter was valued at when the plant stood still
  idle: boolean; // the plant stood still, whatever the rule said
  followed: boolean; // "off" quarter in which the plant did stand still
  cen: number | null;
  cenSettled: boolean;
  gainPln: number | null;
  potentialPln: number | null;
}

export interface CurtailmentTotals {
  decided: number;
  evaluated: number;
  flagged: number;
  hits: number;
  falseAlarms: number;
  misses: number;
  gainPln: number;
  hitGainPln: number;
  falseAlarmPln: number;
  missedPln: number;
  potentialPln: number;
  lostKwh: number;
  followed: number;
}

export interface CurtailmentNext {
  time: string;
  curtail: boolean;
  p50: number | null;
  probNegative: number | null;
}

export interface CurtailmentDay {
  configured: true;
  date: string;
  rule: string;
  quarters: CurtailmentQuarter[];
  totals: CurtailmentTotals;
  next: CurtailmentNext | null;
}

export type CurtailmentResponse = CurtailmentDay | { configured: false };

export interface RecommendationView {
  recommendation: TradeaIndex;
  decidedAt: string;
  expectedSpread: number | null;
  pTail: number | null;
  modelVersion: string;
}

export interface RecommendationRecord {
  days: number;
  croDays: number;
  followedGainPln: number;
  perfectGainPln: number;
  hitRate: number | null;
}

export interface CurtailmentOutlookSlot {
  time: string;
  start: string;
  p50: number;
  probNegative: number | null;
  rdn: number | null; // day-ahead price of the quarter, for context
  state: "off" | "risk" | "on";
}

export interface CurtailmentOutlook {
  issuedAt: string;
  slots: CurtailmentOutlookSlot[];
  windows: { from: string; to: string; maxProbNegative: number | null; minP50: number }[];
}

export interface StrategyView {
  configured: true;
  decidable: { date: string; deadline: string };
  outlook: CurtailmentOutlook | null;
  recommendation: TradeaIndex;
  model: RecommendationView | null;
  record30d: RecommendationRecord;
  record90d: RecommendationRecord;
  currentIndex: TradeaIndex | null;
  tomorrow: {
    date: string;
    ourForecastKwh: number | null;
    tradeaForecastKwh: number | null;
    expectedOursPln: number | null;
    prices: {
      available: boolean;
      source: "tradea" | "tge-hourly" | null;
      mean: number | null;
      min: number | null;
      negativeQuarters: number;
      negativeWindows: { from: string; to: string }[];
    };
  };
  shadowToday: CurtailmentTotals & { next: CurtailmentNext | null };
  shadow7d: {
    gainPln: number;
    hitGainPln: number;
    falseAlarmPln: number;
    missedPln: number;
    potentialPln: number;
    hits: number;
    falseAlarms: number;
    misses: number;
  };
}

export type StrategyResponse = StrategyView | { configured: false };

// Shadow-mode curtailment decisions for one day.
export const useGetCurtailment = (date?: Date, enabled = true) => {
  const dateStr = usePricesDateStr(date);
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;
  return useQuery<CurtailmentResponse>({
    queryKey: [TRADEA.CURTAILMENT(meterId ?? ""), dateStr],
    queryFn: async () => {
      const { data } = await axiosInstance.get<CurtailmentResponse>(TRADEA.CURTAILMENT(meterId!), {
        params: { date: dateStr },
      });
      return data;
    },
    enabled: enabled && Boolean(meterId),
    refetchInterval: 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

// Settlement recommendation, tomorrow's outlook and shadow results.
export const useGetStrategy = () => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;
  return useQuery<StrategyResponse>({
    queryKey: [TRADEA.STRATEGY(meterId ?? "")],
    queryFn: async () => {
      const { data } = await axiosInstance.get<StrategyResponse>(TRADEA.STRATEGY(meterId!));
      return data;
    },
    enabled: Boolean(meterId),
    // The outlook moves with every quarter; keep it within a minute of the clock.
    refetchInterval: 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};
